import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Provider } from '@supabase/supabase-js';
import { TurnstileInstance } from '@marsidev/react-turnstile';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { generateRandomString } from 'libs/utils';

import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { TextInput } from 'components/ui/TextInput';
import { Button } from 'components/ui/Button';
import { TurnstileWrapper } from 'components/ui/TurnstileWrapper';
import { LinkedIn } from 'components/svg/LinkedIn';
import { Google } from 'components/svg/Google';

import GrandPursuitLogo from 'assets/grand-pursuit-logo.png';
import PursuitArrow from 'assets/pursuit-arrow.png';
import PursuitDots from 'assets/pursuit-dots.png';
import PursuitPlus from 'assets/pursuit-plus.png';
import PursuitScribble from 'assets/pursuit-scribble.png';
import PursuitSpin from 'assets/pursuit-spin.png';
import PursuitStars from 'assets/pursuit-stars.png';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import {
    Bumper,
    Divider,
    FullBleedScroller,
    HorizontalFlexWrapper,
} from 'styles/shared';
import { AuthForm, OAuthIconWrapper } from 'styles/auth';

export const PursuitSignUp = () => {
    const navigate = useNavigate();

    const { isSignedIn, signUp, signInWithOAuth } = useAuth();
    const { showErrorToast } = useToast();

    const [searchParams] = useSearchParams();

    const [fullName, setFullName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [isSigningUp, setIsSigningUp] = useState<boolean>(false);

    const turnstileRef = useRef<TurnstileInstance>();

    const pursuitWebsiteLink = useMemo(() => {
        const utmSource = searchParams.get('utm_source');
        const utmCampaign = searchParams.get('utm_campaign');
        return `https://pursuit.thegrand.world/membership?utm_source=${
            utmSource == null ? '' : `${utmSource}_`
        }platform&utm_medium=signup${
            utmCampaign == null ? '' : `&utm_campaign=${utmCampaign ?? ''}`
        }`;
    }, [searchParams]);

    const temporaryPassword = useMemo(generateRandomString, []);

    const onFullNameInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setFullName(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onEmailInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setEmail(target.value);

            if (isError) {
                setIsError(false);
            }
        },
        [isError]
    );

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (isSigningUp) {
                return;
            }
            setIsSigningUp(true);

            if (captchaToken == null) {
                showErrorToast('CAPTCHA verification is required to apply');
                setIsSigningUp(false);
                return;
            }

            if (captchaToken != null && !isSignedIn) {
                const error = await signUp?.({
                    email,
                    password: temporaryPassword,
                    captchaToken,
                    fullName,
                    tempGroupSponsorId: '',
                });

                if (error == null) {
                    navigate(
                        `/confirm?email=${encodeURIComponent(email)}&type=email`
                    );
                } else {
                    turnstileRef.current?.reset();
                    setIsError(true);
                    showErrorToast(error.message);
                }
            }

            setIsSigningUp(false);
        },
        [
            captchaToken,
            email,
            fullName,
            isSignedIn,
            isSigningUp,
            navigate,
            showErrorToast,
            signUp,
            temporaryPassword,
        ]
    );

    const onSignInWithOAuth = useCallback(
        (provider: Provider) =>
            async (event: React.FormEvent<HTMLButtonElement>) => {
                event.preventDefault();

                if (isSigningUp) {
                    return;
                }
                setIsSigningUp(true);

                const error = await signInWithOAuth?.(provider);

                if (
                    error != null &&
                    error.message !== 'Auth session missing!'
                ) {
                    turnstileRef.current?.reset();
                    setIsError(true);
                    showErrorToast(error.message);
                }

                setIsSigningUp(false);
            },
        [isSigningUp, showErrorToast, signInWithOAuth]
    );

    return (
        <FullBleedScroller>
            <Bumper />
            <Bumper $shouldHideOnMobile />
            <div>
                <Link to={pursuitWebsiteLink} target="_blank" rel="noreferrer">
                    <img
                        src={GrandPursuitLogo}
                        width={140}
                        alt="grand-pursuit-logo"
                    />
                </Link>
            </div>
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $shouldHideOnDesktop />
            <HorizontalFlexWrapper
                $isWrapping
                $isJustifyContentCenter
                $isAlignItemsStart
            >
                <AuthForm
                    onSubmit={onSubmit}
                    $backgroundColor={transparencies.white60}
                >
                    <Fonts.Heading6
                        $textAlign="center"
                        $color={colors.mountainAsh}
                    >{`Start your application`}</Fonts.Heading6>
                    <Bumper $minHeight={spacing.xLarge} />
                    <Button
                        onClick={onSignInWithOAuth('linkedin_oidc')}
                        $isDisabled={isSigningUp}
                        $style="outline"
                        $isStretch
                    >
                        <OAuthIconWrapper>
                            <LinkedIn />
                        </OAuthIconWrapper>
                        {`Continue with LinkedIn`}
                    </Button>
                    <Bumper />
                    <Button
                        onClick={onSignInWithOAuth('google')}
                        $isDisabled={isSigningUp}
                        $style="outline"
                        $isStretch
                    >
                        <OAuthIconWrapper>
                            <Google />
                        </OAuthIconWrapper>
                        {`Continue with Google`}
                    </Button>
                    <Bumper $minHeight={spacing.small} />
                    <Divider />
                    <Bumper $minHeight={spacing.xxSmall} />
                    <TextInput
                        type="text"
                        id="fullName"
                        name="fullName"
                        onInput={onFullNameInput}
                        label="Full name"
                        required
                        $backgroundColor={transparencies.darkJungle5}
                    />
                    <Bumper />
                    <TextInput
                        type="email"
                        id="email"
                        name="email"
                        onInput={onEmailInput}
                        $isError={isError}
                        label="Email"
                        required
                        $backgroundColor={transparencies.darkJungle5}
                    />
                    <Bumper />
                    <TurnstileWrapper
                        turnstileRef={turnstileRef}
                        onSuccess={setCaptchaToken}
                        $color={transparencies.white80}
                    />
                    <Bumper />
                    <Button
                        type="submit"
                        value="Submit"
                        $isLoading={isSigningUp}
                        $isStretch
                    >
                        {`Next`}
                    </Button>
                    <Bumper />
                    <Link to="/sign-in">
                        <Fonts.Small
                            $isUnderlined
                        >{`Already have an account?`}</Fonts.Small>
                    </Link>
                </AuthForm>
                <Bumper $minWidth={spacing.xxxLarge} $shouldHideOnMobile />
                {/* TODO: replace inline styles */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Bumper
                        $minHeight={spacing.xxxLarge}
                        $shouldHideOnDesktop
                    />

                    <AuthForm
                        as="div"
                        $backgroundColor={transparencies.white40}
                        $isMobilePaddingOverriden
                        // TODO: replace inline styles
                        style={{
                            alignItems: 'start',
                        }}
                    >
                        <Fonts.Heading7>{`Membership Includes`}</Fonts.Heading7>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitStars}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium>{`An exclusive retreat to connect with 70 remarkable women`}</Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitSpin}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium>{`Personalized 1:1 coaching sessions to guide your journey`}</Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitScribble}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium>{`Monthly group coaching to deepen relationships and foster growth`}</Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <HorizontalFlexWrapper $isMarginAuto>
                            <Link
                                to={pursuitWebsiteLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Fonts.Small
                                    $isUnderlined
                                >{`Learn more`}</Fonts.Small>
                            </Link>
                        </HorizontalFlexWrapper>
                    </AuthForm>
                    <Bumper $minHeight={spacing.xxxLarge} />
                    <AuthForm
                        as="div"
                        $backgroundColor={transparencies.white40}
                        $isMobilePaddingOverriden
                        // TODO: replace inline styles
                        style={{
                            alignItems: 'start',
                        }}
                    >
                        <Fonts.Heading7>{`Membership Criteria`}</Fonts.Heading7>
                        <Bumper />
                        <Fonts.Medium>{`We welcome exceptional women who are:`}</Fonts.Medium>
                        <Bumper $minHeight={spacing.xSmall} />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitPlus}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium $isOpenSansSemiBold>
                                {`Bold Entrepreneurs`}
                                <Fonts.Medium>
                                    {` leading industry-changing businesses with purpose`}
                                </Fonts.Medium>
                            </Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitArrow}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium $isOpenSansSemiBold>
                                {`Visionary Leaders`}
                                <Fonts.Medium>
                                    {` who are decision-makers and managing cross-functional teams`}
                                </Fonts.Medium>
                            </Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <HorizontalFlexWrapper>
                            <img
                                src={PursuitDots}
                                width={24}
                                alt="bullet-icon"
                            />
                            <Bumper $minWidth={spacing.xSmall} />
                            <Fonts.Medium $isOpenSansSemiBold>
                                {`Influential Creators`}
                                <Fonts.Medium>
                                    {` with published works and platforms driving change`}
                                </Fonts.Medium>
                            </Fonts.Medium>
                        </HorizontalFlexWrapper>
                        <Bumper />
                        <Fonts.XSmall>{`Pursuit members are expected to have at least one child under 10, and 12+ years of work experience. Exceptions considered case-by-case.`}</Fonts.XSmall>
                        <Bumper />
                        <HorizontalFlexWrapper $isMarginAuto>
                            <Link
                                to={pursuitWebsiteLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Fonts.Small
                                    $isUnderlined
                                >{`Learn more`}</Fonts.Small>
                            </Link>
                        </HorizontalFlexWrapper>
                    </AuthForm>
                    <Bumper />
                    <Bumper $shouldHideOnMobile />
                </div>
            </HorizontalFlexWrapper>
            <Bumper $shouldHideOnMobile />
            <Bumper $shouldHideOnMobile />
            <Bumper />
        </FullBleedScroller>
    );
};
